import { useState, useEffect, useRef } from 'react';
import { useSeller } from '@/components/layout/DashboardLayout';
import { useProfile } from '@/lib/profile-context';
import { sellersAPI } from '@/lib/api';
import { RiskScoreGauge } from '@/components/dashboard/RiskScoreGauge';
import { TopRiskDrivers } from '@/components/dashboard/TopRiskDrivers';
import { RiskTrendChart } from '@/components/dashboard/RiskTrendChart';
import { RecommendedActions } from '@/components/dashboard/RecommendedActions';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { ScoreChangeReasons } from '@/components/dashboard/ScoreChangeReasons';
import { PreemptiveActionsTable } from '@/components/dashboard/PreemptiveActionsTable';
import { PredictiveInsightsBanner } from '@/components/PredictiveInsightsBanner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Activity, ShieldAlert } from 'lucide-react';

function buildRealtimeRiskDrivers(overview) {
  const base = Array.isArray(overview?.top_drivers) ? overview.top_drivers : [];
  const overrides = Array.isArray(overview?.realtime_kpi_overrides) ? overview.realtime_kpi_overrides : [];
  const baseCritical = base.filter((d) => String(d?.severity || '').toLowerCase() === 'critical');
  if (overrides.length === 0) return baseCritical;

  const mapped = overrides
    .filter((o) => String(o?.severity || '').toLowerCase() === 'critical')
    .map((o) => ({
    name: `${o.label} (Live)`,
    contribution: 12,
    severity: o.severity || 'Critical',
  }));

  return [...mapped, ...baseCritical].slice(0, 6);
}

function buildRealtimeScoreReasons(overview) {
  const base = Array.isArray(overview?.score_change_reasons) ? overview.score_change_reasons : [];
  const overlay = overview?.realtime_overlay;
  const events = Array.isArray(overlay?.events) ? overlay.events : [];
  const overrides = Array.isArray(overview?.realtime_kpi_overrides) ? overview.realtime_kpi_overrides : [];

  const reasons = [];
  if (typeof overlay?.penalty === 'number' && overlay.penalty > 0) {
    reasons.push({
      reason: `Realtime notification penalty (${events.length} event${events.length === 1 ? '' : 's'})`,
      delta: overlay.penalty,
    });
  }
  overrides.slice(0, 3).forEach((o) => {
    reasons.push({
      reason: `${o.label}: ${o.detail || 'Live signal update'}`,
      delta: String(o.severity || '').toLowerCase() === 'critical' ? 8 : 4,
    });
  });

  return [...reasons, ...base].slice(0, 8);
}

export default function OverviewPage() {
  const { hasSelection } = useProfile();
  const { currentSellerId, syncVersion, syncing } = useSeller();
  const [overview, setOverview] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const lastScoreRef = useRef(null);
  const buildRealtimeParams = () => ({
    realtime: 1,
    realtime_hours: 24,
    _ts: Date.now(), // bust proxy/browser cache on live
  });

  useEffect(() => {
    if (!currentSellerId) {
      setOverview(null);
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const [overviewRes, actionsRes] = await Promise.all([
          sellersAPI.getOverview(currentSellerId, buildRealtimeParams()),
          sellersAPI.getPreemptiveActions(currentSellerId),
        ]);
        setOverview(overviewRes.data);
        setActions(actionsRes.data);

        const scoreNow = overviewRes.data?.risk_score;
        if (typeof scoreNow === 'number') {
          const prev = lastScoreRef.current;
          if (typeof prev === 'number' && prev !== scoreNow) {
            console.log('[Realtime] Score changed', { from: prev, to: scoreNow });
          }
          lastScoreRef.current = scoreNow;
        }
      } catch (err) {
        console.error('Failed to load overview:', err);
        setOverview(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentSellerId, syncVersion]);

  // Realtime polling: keep score/alerts fresh between report syncs.
  useEffect(() => {
    if (!currentSellerId) return;
    const id = setInterval(async () => {
      try {
        const res = await sellersAPI.getOverview(currentSellerId, buildRealtimeParams());
        setOverview(res.data);
      } catch (err) {
        // keep quiet; next tick will retry
      }
    }, 30000);
    return () => clearInterval(id);
  }, [currentSellerId, syncVersion]);

  if (hasSelection && syncing) {
    return (
      <div className="space-y-6" data-testid="overview-syncing">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-40" /><Skeleton className="h-40" /><Skeleton className="h-40" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" /><Skeleton className="h-60" />
        </div>
      </div>
    );
  }

  if (!currentSellerId) {
    return (
      <div className="rounded-lg border border-border/60 bg-muted/30 p-6 text-sm text-muted-foreground" data-testid="overview-no-seller">
        <p className="font-medium text-foreground">No seller data yet</p>
        <p className="mt-2">
          {hasSelection
            ? 'We could not load a seller for this account and profile. Try another selection or check Amazon account connectivity.'
            : 'Choose an account and profile in the sidebar to load this dashboard.'}
        </p>
      </div>
    );
  }

  if (loading || !overview) {
    return (
      <div className="space-y-6" data-testid="overview-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-40" /><Skeleton className="h-40" /><Skeleton className="h-40" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" /><Skeleton className="h-60" />
        </div>
      </div>
    );
  }

  const mergedTopDrivers = buildRealtimeRiskDrivers(overview);
  const mergedScoreReasons = buildRealtimeScoreReasons(overview);

  return (
    <div className="space-y-6" data-testid="overview-page">
      {/* Page Title */}
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground">
          Enforcement Risk Score & account health for{' '}
          <span className="text-foreground/90 font-medium">{overview.name}</span>
        </p>
        {overview?.realtime_overlay?.to && (
          <p className="text-xs text-muted-foreground mt-1">
            Last realtime update: {new Date(overview.realtime_overlay.to).toLocaleString()}
          </p>
        )}
      </div>

      {/* Realtime banner */}
      {(() => {
        const overlay = overview.realtime_overlay;
        const overrides = Array.isArray(overview.realtime_kpi_overrides) ? overview.realtime_kpi_overrides : [];
        const events = Array.isArray(overlay?.events) ? overlay.events : [];
        const hasRealtime = (overlay && overlay.enabled && (overlay.penalty > 0 || events.length > 0)) || overrides.length > 0;
        if (!hasRealtime) return null;

        const critical = overview.realtime_alert_counts?.critical ?? 0;
        const warning = overview.realtime_alert_counts?.warning ?? 0;
        const shownEvents = events.slice(0, 5);

        return (
          <Card className="bg-card border-border/80">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[hsl(24,94%,56%)]" />
                    <p className="text-sm font-semibold">Realtime changes detected</p>
                    {critical > 0 && <Badge variant="destructive" className="text-[10px]">Critical {critical}</Badge>}
                    {warning > 0 && <Badge variant="secondary" className="text-[10px]">Warning {warning}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Based on the latest notifications (last {overlay?.window_hours ?? 6}h). Score updates without waiting for report sync.
                  </p>
                </div>
                {typeof overlay?.penalty === 'number' && overlay.penalty > 0 && (
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Realtime penalty</p>
                    <p className="text-sm font-semibold tabular-nums">-{overlay.penalty} pts</p>
                  </div>
                )}
              </div>

              {overrides.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-foreground/90">KPI overrides</p>
                  <div className="flex flex-wrap gap-2">
                    {overrides.slice(0, 6).map((o) => (
                      <Badge
                        key={o.key}
                        variant={String(o.severity).toLowerCase() === 'critical' ? 'destructive' : 'secondary'}
                        className="text-[10px]"
                        title={o.detail}
                      >
                        {o.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {shownEvents.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-foreground/90">Latest events</p>
                  <ul className="space-y-1">
                    {shownEvents.map((e, idx) => (
                      <li key={`${e.key}-${idx}`} className="text-xs text-muted-foreground">
                        <span className="text-foreground/90">{e.title}</span>
                        {e.asin ? <span className="ml-2 font-mono text-[hsl(188,100%,55%)]">{e.asin}</span> : null}
                        {e.order_id ? <span className="ml-2 font-mono">{e.order_id}</span> : null}
                        {e.detail ? <span className="ml-2">· {e.detail}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {Array.isArray(overview.predictive_insights) && overview.predictive_insights.length > 0 && (
        <PredictiveInsightsBanner insights={overview.predictive_insights} />
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-card border-border/80">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="w-10 h-10 rounded-lg bg-[hsl(188,100%,55%)]/10 flex items-center justify-center">
              <Activity className="w-5 h-5 text-[hsl(188,100%,55%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums" data-testid="total-metrics">{overview.total_metrics}</p>
              <p className="text-xs text-muted-foreground">Metrics Monitored</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/80">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="w-10 h-10 rounded-lg bg-[hsl(24,94%,56%)]/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-[hsl(24,94%,56%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums" data-testid="breached-metrics">{overview.metrics_breached}</p>
              <p className="text-xs text-muted-foreground">Metrics in Breach</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/80">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="w-10 h-10 rounded-lg bg-[hsl(0,84%,60%)]/10 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-[hsl(0,84%,60%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums" data-testid="critical-alerts">{overview.critical_alerts}</p>
              <p className="text-xs text-muted-foreground">Critical Alerts</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enforcement Risk Score + Top Drivers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border/80">
          <CardContent className="p-6">
            <RiskScoreGauge
              score={overview.risk_score}
              projectedScore={overview.projected_score}
              delta24h={overview.score_delta_24h}
              trendData={overview.trend_data || []}
            />
          </CardContent>
        </Card>
        <TopRiskDrivers drivers={mergedTopDrivers} />
      </div>

      {/* Trend Chart */}
      <RiskTrendChart trendData={overview.trend_data} />

      {/* Action Cards Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ScoreChangeReasons reasons={mergedScoreReasons} />
        <RecommendedActions actions={overview.recommended_actions} />
        <RecentActivity activity={overview.recent_activity} />
      </div>

      {/* Pre-emptive Actions Table */}
      <PreemptiveActionsTable actions={actions} />
    </div>
  );
}
